import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "./lib";
import { MAPS_URL } from "./data";

const LINKS = [
  ["Menu", "menu"],
  ["Story", "story"],
  ["Reviews", "reviews"],
  ["Visit", "visit"],
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-charcoal/80 backdrop-blur-md border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              scrollToId("home");
            }}
            className="flex items-baseline gap-2 group"
          >
            <span className="font-display font-bold text-2xl tracking-wide text-bone group-hover:text-ember transition-colors duration-300">
              NUUN
            </span>
            <span className="font-arabic text-2xl text-ember leading-none">نون</span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map(([label, id]) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`nav-link-${id}`}
                onClick={go(id)}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim hover:text-bone transition-colors duration-300"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-directions"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberglow text-charcoal font-semibold text-xs tracking-widest uppercase px-5 py-2.5 transition-all duration-300 hover:-translate-y-0.5"
            >
              Directions
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden text-bone p-2"
            >
              {open ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-charcoal/95 backdrop-blur-lg flex flex-col items-center justify-center gap-10 md:hidden"
          >
            {LINKS.map(([label, id], i) => (
              <motion.a
                key={id}
                href={`#${id}`}
                data-testid={`nav-mobile-link-${id}`}
                onClick={go(id)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                className="font-display text-5xl text-bone hover:text-ember transition-colors"
              >
                {label}
              </motion.a>
            ))}
            <motion.a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-directions"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="mt-6 rounded-full bg-ember text-charcoal font-semibold text-sm tracking-widest uppercase px-8 py-4"
            >
              Get Directions
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
